import {Component, Inject, Input, OnChanges, SimpleChanges} from '@angular/core';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {FreeZone} from '@domain/production-schedule/free-zone/free-zone';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {RestZone} from '@domain/production-schedule/rest-zone/rest-zone';
import {ZoneType} from '@domain/zone-type.enum';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {ProductionScheduleBuilderAspectRatio} from '@presentation/pages/texfab/production-schedule/add/plan/builder/production-schedule-builder-aspect-ratio';
import {Zone} from '@presentation/pages/texfab/production-schedule/add/plan/builder/zone';
import {EditZoneComponent} from '@presentation/pages/texfab/production-schedule/add/plan/modals/edit-zone/edit-zone.component';
import {ProductionSchedulePlan, PRODUCTION_SCHEDULE_PLAN} from '@presentation/pages/texfab/production-schedule/add/plan/production-schedule-plan';
import {BaseComponent, DialogBuilderFactoryService} from '@vdw/angular-component-library';
import {each, isEmpty, isEqual, isNil, multiply, reverse, slice, subtract, sum} from 'lodash-es';
import {takeUntil} from 'rxjs/operators';

interface ZoneIndicatorPosition {
  left: number;
  right: number;
}

@Component({
  selector: 'app-production-schedule-zone',
  templateUrl: './production-schedule-zone.component.html',
  styleUrls: ['./production-schedule-zone.component.scss']
})
export class ProductionScheduleZoneComponent extends BaseComponent implements OnChanges {
  @Input() public productionScheduleBuilderWidth: number;
  @Input() public zones: Zone[];
  @Input() public suggestedZones: Zone[];
  @Input() public aspectRatio: ProductionScheduleBuilderAspectRatio;
  @Input() public productionSchedule: ProductionSchedule;

  public readonly zoneIndicatorWidth = 8;

  private selectedZone: Zone;
  private selectedZoneIndex: number;
  private zoneIndicatorPositions: ZoneIndicatorPosition[];

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService
  ) {
    super();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('zones' in changes || 'aspectRatio' in changes) {
      this.setZoneIndicatorsPosition();
    }
  }

  public getZoneIndicatorPosition(zoneIndex: number): number {
    return subtract(this.zoneIndicatorPositions[zoneIndex].left, this.zoneIndicatorWidth);
  }

  public onClickZoneIndicator(zoneIndex: number, zoneIndicatorContainerElement: Element): void {
    this.selectedZone = this.zones[zoneIndex];
    this.selectedZoneIndex = zoneIndex;
    this.productionSchedulePlan.setSelectedZone(this.selectedZone);

    this.dialogBuilderFactoryService
      .getBuilder()
      .withClass('reposition-dialog')
      .withWidth('254px')
      .openDialog(RepositionDialogComponent, {
        component: EditZoneComponent,
        sourceElement: zoneIndicatorContainerElement,
        positionOfDialog: PositionOfDialog.TOP,
        horizontalOffset: 97,
        productionSchedule: this.productionSchedule,
        suggestedZone: this.getSuggestedZone(zoneIndex),
        zone: this.selectedZone,
        zoneIndex,
        zoneType: this.getZoneType(this.selectedZone)
      })
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe(() => {
        this.deselectZoneInProductionScheduleBuilder();
      });
  }

  public getZoneIcon(zoneIndex: number): string {
    const zone = this.zones[zoneIndex];
    const zoneType: ZoneType = this.getZoneType(zone);

    let result = `${zoneType}-zone`;

    if (this.isZoneSelected(zoneIndex)) {
      result = `${zoneType}-zone-selected`;
    } else if (this.hasZoneBeenModified(zone, zoneIndex)) {
      if (this.hasZoneErrors(zone) || this.hasZoneWarnings(zone)) {
        result = `${zoneType}-zone-modified-with-errors`;
      } else {
        result = `${zoneType}-zone-modified`;
      }
    }

    return result;
  }

  public isZonesInstanceOfRestZone(zone: Zone): boolean {
    return zone instanceof RestZone;
  }

  public isZonesInstanceOfFreeZone(zone: Zone): boolean {
    return zone instanceof FreeZone;
  }

  public canShowZoneIndicators(): boolean {
    return !isEmpty(this.zoneIndicatorPositions);
  }

  public hasZoneBeenModified(zone: Zone, zoneIndex: number): boolean {
    return !isEqual(zone.technicalWidthInDents, this.suggestedZones[zoneIndex].technicalWidthInDents);
  }

  public hasZoneErrors(zone: Zone): boolean {
    return !isNil(zone.errors);
  }

  public hasZoneWarnings(zone: Zone): boolean {
    return !isNil(zone.warnings);
  }

  public getZoneWidth(zone: Zone): string {
    return `${zone.technicalWidthInDents}`;
  }

  public isZoneSelected(zoneIndex: number): boolean {
    return isEqual(zoneIndex, this.selectedZoneIndex);
  }

  private calculateZoneIndicatorPosition(zone: Zone): number {
    return zone.startDent + zone.technicalWidthInDents / 2;
  }

  private deselectZoneInProductionScheduleBuilder(): void {
    this.selectedZone = null;
    this.selectedZoneIndex = null;
    this.productionSchedulePlan.setSelectedZone(this.selectedZone);
  }

  private setZoneIndicatorsPosition(): void {
    this.zoneIndicatorPositions = [];

    each(this.zones, (zone: Zone) => {
      const left = multiply(this.calculateZoneIndicatorPosition(zone), this.aspectRatio.width);
      const right = sum([left, this.zoneIndicatorWidth * 2]);
      this.zoneIndicatorPositions.push({left, right});
    });

    let currentMaxRightPosition = this.productionScheduleBuilderWidth;

    each(reverse(slice(this.zoneIndicatorPositions)), (zoneIndicatorPosition: ZoneIndicatorPosition) => {
      if (zoneIndicatorPosition.right > currentMaxRightPosition) {
        zoneIndicatorPosition.right = currentMaxRightPosition;
        zoneIndicatorPosition.left = zoneIndicatorPosition.right - this.zoneIndicatorWidth * 2;
      }

      currentMaxRightPosition = zoneIndicatorPosition.left - this.zoneIndicatorWidth;
    });
  }

  private getSuggestedZone(zoneIndex: number): Zone {
    return this.suggestedZones[zoneIndex];
  }

  private getZoneType(zone: Zone): ZoneType {
    let result: ZoneType;

    if (this.isZonesInstanceOfRestZone(zone)) {
      result = ZoneType.REST;
    } else if (this.isZonesInstanceOfFreeZone(zone)) {
      result = ZoneType.FREE;
    }

    return result;
  }
}
